import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import {ISubItemAttachment, SubItemAttachmentFormValues} from '../../models/SubItems/SubItemAttachment'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'
import dayjs from 'dayjs'


export default class SubItemAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.subItemAttachmentRegistry.clear()
                this.loadSubItemAttachment()
            }
        )
        reaction(
            () => this.currentSubItemId,
            () => {
                this.page = 0
                this.subItemAttachmentRegistry.clear()
                !!this.currentSubItemId ? this.getSubItemAttachmentsBySubItemId() : this.loadSubItemAttachment()
            }
        )
    }

    subItemAttachmentRegistry = new Map()
    subItemAttachment: ISubItemAttachment | null = null
    editMode = false


    currentSubItemId = '';
    subItemAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentSubItemId = (subItemId: string) => {
        this.currentSubItemId = subItemId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!predicate || predicate === '') {
            this.predicate.clear()
        } else {
            this.predicate.delete(predicate)
            if (!!value)
                this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.subItemAttachmentCount / this.limit)
    }

    get subItemAttachmentsById() {
        return Array.from(this.subItemAttachmentRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get subItemAttachmentsByDate() {
        return Array.from(this.subItemAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1
        )
    }

    getSubItemAttachment = (id: string) => {
        return this.subItemAttachmentRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.subItemAttachmentRegistry.clear()
        this.loadSubItemAttachment()
    }

    clearSubItemAttachment = () => {
        this.subItemAttachmentRegistry.clear()
    }

    loadSubItemAttachment = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            if (!!this.currentSubItemId) {
                this.getSubItemAttachmentsBySubItemId()
            }else{
                const subItemAttachmentsEnvelope = await Agent.SubItemAttachments.list(this.axiosParams);
                const {modelsDto, modelCount} = subItemAttachmentsEnvelope;

                runInAction(() => {
                    this.clearSubItemAttachment();
                    modelsDto.forEach((subItemAttachment: ISubItemAttachment) => {
                        this.subItemAttachmentRegistry.set(subItemAttachment.id, subItemAttachment)
                    })
                    this.subItemAttachmentCount = modelCount
                })
            }
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getSubItemAttachmentById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let subItemAttachment = await Agent.SubItemAttachments.byId(id)
            runInAction(() => {
                this.subItemAttachment = subItemAttachment
                this.subItemAttachmentRegistry.set(subItemAttachment.id, subItemAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            return subItemAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getSubItemAttachmentsBySubItemId = async (subItemId?: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let subItemAttachmentEnvelope = await Agent.SubItemAttachments.bySubItemId(subItemId || this.currentSubItemId, this.axiosParams)
            const {modelsDto, modelCount} = subItemAttachmentEnvelope
            runInAction( () => {
                modelsDto.forEach(subItemAttachment => {
                    this.subItemAttachmentRegistry.set(subItemAttachment.id, subItemAttachment)
                })

                this.subItemAttachmentCount = modelCount
            })
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction( () => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    createSubItemAttachment = async (subItemAttachment: SubItemAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            const newAttachment = await Agent.SubItemAttachments.create(subItemAttachment)
            await this.loadSubItemAttachment()

            runInAction( () => {
                this.subItemAttachmentRegistry.set(newAttachment.id, newAttachment)
                this.rootStore.modalStore.closeModal('AttachmentModal')
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.subItemStore.markSubItemAsAttachedById(subItemAttachment.subItemId)
            })

            toast.success('Saved')
            return newAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction( () => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    deleteSubItemAttachment = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const newAttachment = await Agent.SubItemAttachments.delete(id ?? '')
            runInAction( () => {
                this.subItemAttachmentRegistry.delete(newAttachment)
            })
            toast.success('Successfully deleted!')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction( () => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }


}